import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { Table, Input, notification, DatePicker } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception, success } from '@extensions/notification';
import { getDate, delayAction, toFinanceString } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { ITruckStatistic } from '@entities/truck-statistic';
import { ITruckFilter } from '@entities/truck-filter';

import { TruckType } from '@enums/truck-type';
import { TruckStatus, enumLabel as truckStatusLabel } from '@enums/truck-status';

const filterContext: string = 'ImportTrucksStatistic';

interface IImportTrucksStatisticProps extends IBaseComponentProps {
    useDiscounts: boolean;
}

const ImportTrucksStatistic = (props: IImportTrucksStatisticProps) => {
    const { serverFetch, useDiscounts } = props;

    const initFilter: ITruckFilter = {
        type: TruckType.Import,
        statuses: [TruckStatus.OnWay, TruckStatus.Finished],
    };

    const { RangePicker } = DatePicker;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [api, contextHolder] = notification.useNotification();

    const filter = useAppSelector<ITruckFilter>((s) => s.filters[filterContext]);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [trucks, setTrucks] = useState<Array<ITruckStatistic>>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [calculating, setCalculating] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            setTrucks([]);

            const promises = [
                await serverFetch('statistics/trucks', { method: 'GET', queryParams: filter })
                    .then((data: Array<ITruckStatistic>) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения статистики по импорту', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setTrucks(result[0][0] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const onCalculate = () => {
        setCalculating(true);

        serverFetch(`statistics/trucks/calc`, { method: 'POST' })
            .then(() => {
                success(api, 'Перерасчет статистикки по импорту успешно завершен');
                setCalculating(false);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                setCalculating(false);
                exception(api, 'Ошибка перерасчета статистики по импорту', ex, () => d(userLoaded(undefined)));
            });
    };

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Запустить процесс перерасчета',
                        key: 'calc',
                        disabled: loading,
                        loading: calculating,
                        icon: <PlayCircleOutlined />,
                        onClick: () => {
                            onCalculate();
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 150 }}
                            key='number'
                            placeholder='Машина'
                            value={filter?.number}
                            onChange={(data) => {
                                d(setFilter({ ...filter, number: data.target.value }, filterContext));
                            }}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='departure'
                            format='DD.MM.YYYY'
                            placeholder={['Отгрузка с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.departureFrom ? dayjs(filter?.departureFrom) : null,
                                filter?.departureTo ? dayjs(filter?.departureTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                departureFrom: undefined,
                                                departureTo: undefined,
                                            },
                                            filterContext
                                        )
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            departureFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            departureTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext
                                    )
                                );
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ITruckStatistic> = [
            {
                title: 'Машина',
                dataIndex: 'number',
                key: 'number',
                width: 150,
            },
            {
                title: 'Отгрузка',
                width: 100,
                align: 'center',
                render: (_, record) => {
                    return getDate(record.departureOn);
                },
            },
            {
                title: 'Приемка',
                width: 100,
                align: 'center',
                render: (_, record) => {
                    return getDate(record.finishedOn);
                },
            },
            {
                title: 'Кол-во / шт',
                dataIndex: 'totalQty',
                key: 'totalQty',
                width: 120,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 700,
                    },
                }),
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                width: 120,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return record.totalVolume?.toFixed(2);
                },
            },
            {
                title: 'Брутто / кг',
                width: 150,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#e5e5ff',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return record.grossWeight?.toFixed(0);
                },
            },
            {
                title: 'Транспорт ($)',
                width: 150,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: 'var(--main-green)',
                        fontWeight: 700,
                        color: '#FFFFFF',
                    },
                }),
                render: (_, record) => {
                    return toFinanceString(record.totalPriceTruck, 2);
                },
            },
            {
                title: 'Фрахт ($)',
                width: 150,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#e5e5ff',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return toFinanceString(record.charteringAmountUsd, 2);
                },
            },
            {
                title: 'Таможня ($)',
                width: 150,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#e5e5ff',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return toFinanceString(record.customsAmountUsd, 2);
                },
            },
            {
                title: 'Коммерция ($)',
                width: 150,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#e5e5ff',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return toFinanceString(record.commerceAmountUsd, 2);
                },
            },
            {
                title: 'Скидка ($)',
                width: 150,
                align: 'center',
                hidden: !useDiscounts,
                onCell: () => ({
                    style: {
                        background: '#e5e5ff',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return toFinanceString(record.totalDiscount, 2);
                },
            },
            {
                title: 'ИТОГО ($)',
                width: 150,
                align: 'center',
                onCell: (record) => {
                    return {
                        style: {
                            background: record.total >= 0 ? 'var(--main-green)' : 'var(--main-red)',
                            fontWeight: 700,
                            color: '#FFFFFF',
                        },
                    };
                },
                render: (_, record) => {
                    return toFinanceString(record.total, 2);
                },
            },
            { title: 'Перевозчик', dataIndex: 'cargoCarrier', key: 'cargoCarrier', width: 200 },

            { title: 'Маршрут', dataIndex: 'waypointHeaderName', key: 'waypointHeaderName', width: 200 },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                dataSource={trucks}
                pagination={false}
                scroll={{ y: height, x: 'max-content' }}
            />
        );
    };
    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {contextHolder}
        </div>
    );
};

export default ImportTrucksStatistic;
